/**
 * @fileoverview File des remboursements backoffice.
 *
 * La page affiche la file des demandes. La création et la validation quatre
 * yeux sont des tâches secondaires, ouvertes dans des `Sheet` pour garder la
 * liste lisible.
 */

import { useMemo, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Amount,
  Button,
  DataCard,
  DataTable,
  EmptyState,
  FileInput,
  FormField,
  PageHeader,
  ReasonTextarea,
  SearchInput,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  StatusBadge,
  WarningNotice,
} from '@gnpc/ui';
import { CheckCircle2, Paperclip, Plus, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  ADMIN_PAYMENTS,
  CURRENT_AGENT,
  REFUNDS,
  formatDateTime,
  maskPhone,
  operatorLabel,
  type RefundRequest,
} from '../data/backoffice.ts';
import { CapabilityGuard } from '../features/auth/CapabilityProvider.tsx';

/**
 * Critères de filtrage de la file des remboursements.
 *
 * On reprend le pattern « brouillon / appliqué » de {@link PaymentsPage} : les
 * champs éditent `draftFilters` et ne sont pris en compte qu'à la validation du
 * formulaire (`appliedFilters`). Le jour où la file sera servie par l'API, le
 * remplacement du mock par un curseur opaque n'impacte pas cette surface UI.
 */
interface RefundFilters {
  /** Texte libre appliqué à la référence, à l'id du paiement et à l'email du demandeur. */
  query: string;
  /** Statut de remboursement (`RefundStatus`) ou `ALL` pour ne pas filtrer. */
  statusFilter: string;
}

/** Instant de référence (mock) pour le calcul du SLA des remboursements. */
const REFERENCE_NOW = new Date('2026-07-14T12:00:00Z').getTime();
/** Délai cible (heures) de traitement d'une demande de remboursement (C-21). */
const REFUND_SLA_HOURS = 48;

/**
 * SLA d'une demande en attente : temps restant avant échéance, ou dépassement.
 * `null` pour les demandes déjà traitées. Observation Quipux QFS (C-21).
 */
function formatRefundSla(refund: RefundRequest): { label: string; breached: boolean } | null {
  if (refund.status !== 'REQUESTED') return null;
  const dueMs = new Date(refund.createdAt).getTime() + REFUND_SLA_HOURS * 3_600_000;
  const remainingMs = dueMs - REFERENCE_NOW;
  if (remainingMs <= 0) {
    return { label: `En retard ${Math.ceil(-remainingMs / 3_600_000)} h`, breached: true };
  }
  return { label: `Reste ${Math.floor(remainingMs / 3_600_000)} h`, breached: false };
}

const columns: ColumnDef<RefundRequest>[] = [
  { accessorKey: 'id', header: 'Référence', cell: ({ row }) => <code>{row.original.id}</code> },
  { accessorKey: 'paymentId', header: 'Paiement', cell: ({ row }) => <code>{row.original.paymentId}</code> },
  { accessorKey: 'amount', header: 'Montant', cell: ({ row }) => <Amount value={row.original.amount} /> },
  {
    accessorKey: 'status',
    header: 'Statut',
    cell: ({ row }) => <StatusBadge kind="refund" status={row.original.status} compact />,
  },
  {
    id: 'sla',
    header: 'SLA',
    cell: ({ row }) => {
      const sla = formatRefundSla(row.original);
      if (!sla) return <span className="text-muted-foreground text-sm">—</span>;
      return (
        <span
          className={
            sla.breached
              ? 'text-status-failed-800 text-sm font-medium'
              : 'text-muted-foreground text-sm'
          }
        >
          {sla.label}
        </span>
      );
    },
  },
  { accessorKey: 'requester', header: 'Demandeur' },
  {
    accessorKey: 'approver',
    header: 'Valideur',
    cell: ({ row }) => row.original.approver ?? <span className="text-muted-foreground">—</span>,
  },
  { accessorKey: 'createdAt', header: 'Créé le', cell: ({ row }) => formatDateTime(row.original.createdAt) },
];

interface RefundReviewCopy {
  title: string;
  description: string;
  summaryTitle: string;
  summaryDescription: (refund: RefundRequest) => string;
}

const REFUND_REVIEW_COPY: Record<RefundRequest['status'], RefundReviewCopy> = {
  REQUESTED: {
    title: 'Validation quatre yeux',
    description: 'Contrôler la demande avant approbation.',
    summaryTitle: 'Validation requise',
    summaryDescription: () => "Ajoutez un motif d'analyse avant d'approuver la demande.",
  },
  APPROVED: {
    title: 'Demande approuvée',
    description: "La demande est validée et n'est plus modifiable.",
    summaryTitle: 'Validation terminée',
    summaryDescription: (refund) =>
      refund.approver
        ? `Approuvée par ${refund.approver}.`
        : 'Approuvée par un agent habilité.',
  },
  EXECUTED: {
    title: 'Remboursement exécuté',
    description: 'Le remboursement est finalisé.',
    summaryTitle: 'Traitement terminé',
    summaryDescription: () => "Aucune action supplémentaire n'est disponible pour cette demande.",
  },
  REJECTED: {
    title: 'Demande rejetée',
    description: "La demande a été refusée et n'est plus approuvable.",
    summaryTitle: 'Validation refusée',
    summaryDescription: () => 'Aucune approbation ne peut être lancée depuis ce statut.',
  },
};

function getRefundRowActionLabel(refund: RefundRequest): string {
  return refund.status === 'REQUESTED'
    ? `Valider la demande ${refund.id}`
    : `Consulter la demande ${refund.id}`;
}

export function RefundsPage(): JSX.Element {
  const refundablePayments = useMemo(
    () =>
      ADMIN_PAYMENTS.filter(
        (payment) =>
          payment.paymentStatus === 'PAID' &&
          (payment.sgiStatus === 'FAILED' || payment.sgiStatus === 'PARTIAL'),
      ),
    [],
  );
  const [selectedPaymentId, setSelectedPaymentId] = useState(refundablePayments[0]?.id ?? '');
  const [paymentQuery, setPaymentQuery] = useState('');
  const filteredRefundablePayments = useMemo(() => {
    const normalizedQuery = paymentQuery.trim().toLowerCase();
    const normalizedPhoneQuery = paymentQuery.replace(/\s/g, '');

    if (!normalizedQuery) return refundablePayments;

    return refundablePayments.filter((payment) => {
      return (
        payment.plate.toLowerCase().includes(normalizedQuery) ||
        payment.merchantReference.toLowerCase().includes(normalizedQuery) ||
        payment.userPhone.includes(normalizedPhoneQuery)
      );
    });
  }, [paymentQuery, refundablePayments]);
  const selectedPayment = filteredRefundablePayments.find((payment) => payment.id === selectedPaymentId);

  // Filtres de la file (indépendants de la recherche de paiement du Sheet de
  // création ci-dessus). Brouillon éditable + valeurs appliquées, comme sur
  // PaymentsPage, pour un comportement identique une fois branché au backend.
  const initialRefundFilters: RefundFilters = { query: '', statusFilter: 'ALL' };
  const [draftFilters, setDraftFilters] = useState<RefundFilters>(initialRefundFilters);
  const [appliedFilters, setAppliedFilters] = useState<RefundFilters>(initialRefundFilters);

  const updateDraftFilter = <K extends keyof RefundFilters>(key: K, value: RefundFilters[K]) => {
    setDraftFilters((current) => ({ ...current, [key]: value }));
  };

  const filteredRefunds = useMemo(() => {
    const normalizedQuery = appliedFilters.query.toLowerCase();
    return REFUNDS.filter((refund) => {
      const matchesQuery =
        normalizedQuery.length === 0 ||
        refund.id.toLowerCase().includes(normalizedQuery) ||
        refund.paymentId.toLowerCase().includes(normalizedQuery) ||
        refund.requester.toLowerCase().includes(normalizedQuery);
      const matchesStatus =
        appliedFilters.statusFilter === 'ALL' || refund.status === appliedFilters.statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [appliedFilters]);

  // Distingue « file réellement vide » (message positif conservé) de « filtres
  // trop restrictifs » (message neutre + réinitialisation), sur les valeurs
  // appliquées puisque `filteredRefunds` en découle.
  const hasActiveFilters =
    appliedFilters.query.length > 0 || appliedFilters.statusFilter !== 'ALL';

  // Montant immobilisé (C-21) = demandes en cours (demandées + approuvées non
  // encore exécutées) ; alimente la synthèse au-dessus de la file.
  const immobilizedAmount = REFUNDS.filter(
    (refund) => refund.status === 'REQUESTED' || refund.status === 'APPROVED',
  ).reduce((sum, refund) => sum + refund.amount, 0);
  const pendingCount = REFUNDS.filter((refund) => refund.status === 'REQUESTED').length;

  const applyFilters = () => {
    setAppliedFilters({ ...draftFilters, query: draftFilters.query.trim() });
  };

  const resetFilters = () => {
    setDraftFilters(initialRefundFilters);
    setAppliedFilters(initialRefundFilters);
  };

  const [selectedRefundId, setSelectedRefundId] = useState(REFUNDS[0]?.id ?? '');
  const [approvalSheetOpen, setApprovalSheetOpen] = useState(false);
  const selectedRefund = REFUNDS.find((refund) => refund.id === selectedRefundId) ?? REFUNDS[0];
  const selectedRefundPayment = selectedRefund
    ? ADMIN_PAYMENTS.find((payment) => payment.id === selectedRefund.paymentId)
    : undefined;
  const selectedRefundReview = selectedRefund ? REFUND_REVIEW_COPY[selectedRefund.status] : undefined;
  const canApproveSelectedRefund = selectedRefund?.status === 'REQUESTED';
  /**
   * Règle des 4 yeux : le demandeur (`refund.requester`, email de l'agent)
   * ne peut jamais approuver sa propre demande, même avec la capacité
   * `refund:approve`. Le backend refera ce contrôle côté serveur.
   */
  const isOwnRefundRequest = selectedRefund?.requester === CURRENT_AGENT.email;
  const [requestReason, setRequestReason] = useState(
    "Paiement confirmé avec une ligne non enregistrée côté Quipux.",
  );
  const [approvalReason, setApprovalReason] = useState(
    'Analyse conforme : ligne non enregistrable chez Quipux.',
  );
  /** Nom de la pièce justificative jointe à la nouvelle demande (C-19, obligatoire). */
  const [attachmentName, setAttachmentName] = useState('');

  return (
    <>
      <Sheet>
        <div className="grid gap-6">
          <PageHeader
            eyebrow="Remboursements"
            title="Traiter les remboursements"
            description="File opérationnelle des demandes liées aux paiements partiellement ou définitivement non enregistrés."
            actions={
              <CapabilityGuard capability="refund:request">
                <SheetTrigger asChild>
                  <Button>
                    <Plus className="size-4" />
                    Demander un remboursement
                  </Button>
                </SheetTrigger>
              </CapabilityGuard>
            }
          />

          <section className="grid gap-6">
            {/* Synthèse (C-21) : montant immobilisé + demandes en attente. */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="bg-card rounded-lg border px-4 py-3">
                <p className="text-muted-foreground text-xs font-medium uppercase">
                  Montant immobilisé
                </p>
                <p className="text-quipux-navy-950 mt-1 text-xl font-semibold tabular-nums">
                  {immobilizedAmount.toLocaleString('fr-FR')} F CFA
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  Demandes en cours (demandées + approuvées)
                </p>
              </div>
              <div className="bg-card rounded-lg border px-4 py-3">
                <p className="text-muted-foreground text-xs font-medium uppercase">
                  En attente de validation
                </p>
                <p className="text-quipux-navy-950 mt-1 text-xl font-semibold tabular-nums">
                  {pendingCount}
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  SLA cible {REFUND_SLA_HOURS} h · validation 4 yeux
                </p>
              </div>
            </div>

            <DataTable
              columns={columns}
              data={filteredRefunds}
              toolbar={
                <form
                  className="grid w-full items-end gap-3 sm:grid-cols-2 lg:grid-cols-4"
                  onSubmit={(event) => {
                    event.preventDefault();
                    applyFilters();
                  }}
                >
                  <FormField label="Recherche" className="lg:col-span-2">
                    <SearchInput
                      size="sm"
                      value={draftFilters.query}
                      onChange={(event) => updateDraftFilter('query', event.target.value)}
                      onClear={() => updateDraftFilter('query', '')}
                      placeholder="Référence, id paiement ou email…"
                    />
                  </FormField>
                  <FormField label="Statut">
                    <select
                      value={draftFilters.statusFilter}
                      onChange={(event) => updateDraftFilter('statusFilter', event.target.value)}
                      className="border-input bg-background h-9 min-w-0 rounded-md border px-3 text-sm"
                    >
                      <option value="ALL">Tous statuts</option>
                      <option value="REQUESTED">Demandé</option>
                      <option value="APPROVED">Approuvé</option>
                      <option value="EXECUTED">Exécuté</option>
                      <option value="REJECTED">Rejeté</option>
                    </select>
                  </FormField>
                  <div className="flex flex-wrap items-center justify-end gap-2 sm:col-span-2 lg:col-span-4">
                    <Button type="submit" size="sm">
                      Rechercher
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={resetFilters}>
                      Réinitialiser
                    </Button>
                  </div>
                </form>
              }
              mobileCardLabel="Demandes de remboursement"
              mobileCard={(refund) => (
                <DataCard
                  eyebrow={refund.id}
                  title={refund.paymentId}
                  description={`Demandé par ${refund.requester}`}
                  aside={<Amount value={refund.amount} />}
                  meta={
                    <>
                      <StatusBadge kind="refund" status={refund.status} compact />
                      <span className="text-muted-foreground">{formatDateTime(refund.createdAt)}</span>
                    </>
                  }
                />
              )}
              rowAction={{
                label: getRefundRowActionLabel,
                onSelect: (refund) => {
                  setSelectedRefundId(refund.id);
                  setApprovalSheetOpen(true);
                },
              }}
              emptyState={
                hasActiveFilters ? (
                  <EmptyState
                    title="Aucune demande dans cette vue"
                    description="Modifiez les filtres ou la recherche pour élargir le périmètre."
                    action={
                      <Button variant="outline" size="sm" onClick={resetFilters}>
                        Réinitialiser les filtres
                      </Button>
                    }
                  />
                ) : (
                  <EmptyState
                    tone="positive"
                    title="Aucun remboursement en attente"
                    description="La file est vide, aucune validation quatre yeux n'est requise."
                  />
                )
              }
              pagination={{ label: `${filteredRefunds.length} demande(s)`, hasNextPage: false }}
            />
          </section>
        </div>

        <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
          <SheetHeader className="border-border border-b p-5 pr-12">
            <SheetTitle>Nouvelle demande</SheetTitle>
            <SheetDescription>
              Créer une demande à partir d'un paiement encaissé à corriger.
            </SheetDescription>
          </SheetHeader>

          <div className="grid gap-4 p-5">
            <FormField label="Rechercher un paiement" required>
              <SearchInput
                size="sm"
                value={paymentQuery}
                onChange={(event) => setPaymentQuery(event.target.value)}
                onClear={() => setPaymentQuery('')}
                placeholder="Plaque, référence ou téléphone…"
              />
            </FormField>

            <div className="grid gap-2" role="listbox" aria-label="Paiements remboursables">
              {filteredRefundablePayments.length > 0 ? (
                filteredRefundablePayments.map((payment) => {
                  const selected = payment.id === selectedPaymentId;
                  return (
                    <button
                      key={payment.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      onClick={() => setSelectedPaymentId(payment.id)}
                      className={`rounded-lg border p-3 text-left transition-colors ${
                        selected
                          ? 'border-primary bg-primary/5'
                          : 'border-border bg-card hover:border-quipux-orange-500/60'
                      }`}
                    >
                      <span className="flex items-start justify-between gap-3">
                        <span className="min-w-0">
                          <span className="text-quipux-navy-950 block text-sm font-semibold">
                            {payment.plate}
                          </span>
                          <code className="mt-1 block truncate text-xs">
                            {payment.merchantReference}
                          </code>
                          <span className="text-muted-foreground mt-1 block text-xs">
                            {maskPhone(payment.userPhone)} · {operatorLabel(payment.operatorCode)}
                          </span>
                        </span>
                        <Amount value={payment.totalAmount} />
                      </span>
                    </button>
                  );
                })
              ) : (
                <div className="rounded-lg border border-border p-4">
                  <p className="text-quipux-navy-950 text-sm font-medium">Aucun paiement trouvé</p>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Essayez une autre plaque, référence ou téléphone.
                  </p>
                </div>
              )}
            </div>

            {selectedPayment && (
              <div className="rounded-lg border border-border p-4 text-sm">
                <p className="text-muted-foreground text-xs uppercase">Montant à rembourser</p>
                <div className="mt-2">
                  <Amount value={selectedPayment.totalAmount} />
                </div>
                <p className="text-muted-foreground mt-1 text-xs">
                  Calculé depuis le paiement sélectionné (ne peut excéder le montant encaissé),
                  non modifiable dans cette demande.
                </p>
              </div>
            )}
            <ReasonTextarea value={requestReason} onChange={(event) => setRequestReason(event.target.value)} />

            {/* Pièce justificative obligatoire (C-19). */}
            <FormField label="Pièce justificative" required>
              <FileInput
                fileName={attachmentName}
                onFileChange={setAttachmentName}
                accept=".pdf,.jpg,.jpeg,.png"
                hint="PDF, JPG ou PNG · 5 Mo max"
              />
            </FormField>
          </div>

          <SheetFooter className="border-border border-t p-5">
            <CapabilityGuard capability="refund:request">
              <SheetClose asChild>
                <Button
                  disabled={
                    !selectedPayment || requestReason.trim().length < 10 || !attachmentName
                  }
                  onClick={() => {
                    if (!selectedPayment) return;
                    toast.success('Demande de remboursement enregistrée', {
                      description: `${selectedPayment.plate} · ${selectedPayment.totalAmount.toLocaleString('fr-FR')} F CFA — en attente de validation quatre yeux.`,
                    });
                    setAttachmentName('');
                  }}
                >
                  <Plus className="size-4" />
                  Enregistrer la demande
                </Button>
              </SheetClose>
            </CapabilityGuard>
            <SheetClose asChild>
              <Button variant="outline">Annuler</Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={approvalSheetOpen} onOpenChange={setApprovalSheetOpen}>
        <SheetContent className="w-[calc(100vw-2rem)] overflow-y-auto p-0 sm:max-w-md">
          <SheetHeader className="border-border border-b p-5 pr-12">
            <SheetTitle>{selectedRefundReview?.title ?? 'Demande de remboursement'}</SheetTitle>
            <SheetDescription>
              {selectedRefundReview?.description ?? 'Consulter la demande sélectionnée.'}
            </SheetDescription>
          </SheetHeader>

          <div className="grid gap-4 p-5">
            {selectedRefund && (
              <div className="grid gap-3">
                <div className="rounded-lg border border-border p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-muted-foreground text-xs uppercase">Demande</p>
                      <code className="mt-1 block text-sm">{selectedRefund.id}</code>
                    </div>
                    <StatusBadge kind="refund" status={selectedRefund.status} compact />
                  </div>
                  <dl className="mt-4 grid gap-3 text-sm">
                    <div>
                      <dt className="text-muted-foreground">Paiement</dt>
                      <dd className="mt-1 font-medium">{selectedRefund.paymentId}</dd>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <dt className="text-muted-foreground">Montant</dt>
                        <dd className="mt-1">
                          <Amount value={selectedRefund.amount} />
                        </dd>
                      </div>
                      <div>
                        <dt className="text-muted-foreground">Créé le</dt>
                        <dd className="mt-1">{formatDateTime(selectedRefund.createdAt)}</dd>
                      </div>
                    </div>
                    <div>
                      <dt className="text-muted-foreground">Demandeur</dt>
                      <dd className="mt-1">{selectedRefund.requester}</dd>
                    </div>
                    {selectedRefund.approver ? (
                      <div>
                        <dt className="text-muted-foreground">Valideur</dt>
                        <dd className="mt-1">{selectedRefund.approver}</dd>
                      </div>
                    ) : null}
                    {selectedRefund.documentName ? (
                      <div>
                        <dt className="text-muted-foreground">Pièce justificative</dt>
                        <dd className="mt-1 flex items-center gap-1.5">
                          <Paperclip className="size-3.5" />
                          {selectedRefund.documentName}
                        </dd>
                      </div>
                    ) : null}
                  </dl>
                </div>

                {selectedRefundPayment && (
                  <div className="rounded-lg border border-border p-4 text-sm">
                    <p className="text-quipux-navy-950 font-medium">{selectedRefundPayment.plate}</p>
                    <p className="text-muted-foreground mt-1">
                      {operatorLabel(selectedRefundPayment.operatorCode)} · {selectedRefundPayment.lineItems.length} ligne(s) de paiement
                    </p>
                  </div>
                )}

                <div className="rounded-lg border border-border p-4 text-sm">
                  <p className="text-muted-foreground text-xs uppercase">Motif de demande</p>
                  <p className="mt-2 text-quipux-navy-950">{selectedRefund.reason}</p>
                </div>
              </div>
            )}

            {selectedRefundReview && selectedRefund ? (
              <div className="rounded-lg border border-border p-4 text-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-muted-foreground text-xs uppercase">État</p>
                    <p className="text-quipux-navy-950 mt-1 font-medium">
                      {selectedRefundReview.summaryTitle}
                    </p>
                  </div>
                  <StatusBadge kind="refund" status={selectedRefund.status} compact />
                </div>
                <p className="text-muted-foreground mt-3">
                  {selectedRefundReview.summaryDescription(selectedRefund)}
                </p>
              </div>
            ) : null}

            {canApproveSelectedRefund && isOwnRefundRequest ? (
              <WarningNotice title="Règle des 4 yeux">
                Vous ne pouvez pas approuver votre propre demande.
              </WarningNotice>
            ) : null}

            {canApproveSelectedRefund ? (
              <ReasonTextarea value={approvalReason} onChange={(event) => setApprovalReason(event.target.value)} />
            ) : null}
          </div>

          <SheetFooter className="border-border border-t p-5">
            {canApproveSelectedRefund ? (
              <CapabilityGuard capability="refund:approve">
                <Button
                  disabled={
                    !selectedRefund || isOwnRefundRequest || approvalReason.trim().length < 10
                  }
                  onClick={() => {
                    if (!selectedRefund) return;
                    toast.success('Demande approuvée', {
                      description: `${selectedRefund.id} · ${selectedRefund.amount.toLocaleString('fr-FR')} F CFA — remboursement transmis pour exécution.`,
                    });
                    setApprovalSheetOpen(false);
                  }}
                >
                  <CheckCircle2 className="size-4" />
                  Approuver la demande
                </Button>
                {/* Rejeter la demande (C-20) — également soumis à la règle 4 yeux. */}
                <Button
                  variant="outline"
                  disabled={
                    !selectedRefund || isOwnRefundRequest || approvalReason.trim().length < 10
                  }
                  onClick={() => {
                    if (!selectedRefund) return;
                    toast.success('Demande rejetée', {
                      description: `${selectedRefund.id} — remboursement refusé. Motif journalisé.`,
                    });
                    setApprovalSheetOpen(false);
                  }}
                >
                  <XCircle className="size-4" />
                  Rejeter la demande
                </Button>
              </CapabilityGuard>
            ) : null}
            <SheetClose asChild>
              <Button variant="outline">{canApproveSelectedRefund ? 'Annuler' : 'Fermer'}</Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
